import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import LazyImage from './LazyImage';
import LazySource from './LazySource';
import tailwindConfig from '../../../tailwind.config';
import useTranslation from '../hooks/useTranslation';
import ContentLightbox from './ContentLightbox';

const CardTitle = props => {
  const { as: Tag = 'h3', children } = props;
  return <Tag className='c-card__title h4'>{children}</Tag>;
};

CardTitle.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node
};

const CardHTMLTag = ({ tag, children, ...props }) => {
  const Tag = tag || 'div';
  return <Tag {...props}>{children}</Tag>;
};

CardHTMLTag.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.node
};

const Card = ({
  headline,
  subheadline,
  description,
  hasLightboxContent,
  lightboxDescription,
  image,
  fromPrice,
  fixPrice,
  url,
  hideButton = false,
  type,
  label,
  openInNewTab,
  headlineLevel
}) => {
  const __ = useTranslation();
  const lightboxRef = useRef(null);
  const buttonRef = useRef(null);
  const buttonText = type === 'room' ? __('view-room-button-text') : __('more');
  const buttonLabel = (
    type === 'room' ? __('view-room-button-text-details') : __('read-more-details')
  ).replace('%s', headline);
  const priceString = useMemo(() => {
    if (fromPrice) {
      return __(`from-price-formatted`, { price: fromPrice });
    } else if (fixPrice) {
      return __(`fix-price-formatted`, { price: fixPrice });
    } else {
      return '';
    }
  }, [fromPrice, fixPrice]);

  return (
    <CardHTMLTag
      className={`c-card ${!image ? 'c-card--no-image' : ''} c-card--${type}`}
      href={url && hideButton ? url : undefined}
      target={openInNewTab && url && hideButton ? '_blank' : undefined}
      tag={url && hideButton ? 'a' : 'div'}
    >
      {image && (
        <LazyImage
          figureClass={'c-card__image'}
          placeholderUrl={image.sizes.placeholder}
          defaultUrl={image.sizes.medium_large}
          alt={image.alt}
          onClick={() => buttonRef.current?.click()}
          onClickTitle={buttonLabel}
        >
          <LazySource
            srcSet={image.sizes.large}
            mediaMin={tailwindConfig.theme.screens.xl + 'px'}
          />
          <LazySource
            srcSet={image.sizes.medium_large}
            mediaMin={tailwindConfig.theme.screens.lg + 'px'}
          />
          <LazySource
            srcSet={image.sizes.large}
            mediaMin={tailwindConfig.theme.screens.md + 'px'}
          />
          <LazySource
            srcSet={image.sizes.medium_large}
            mediaMin={tailwindConfig.theme.screens.sm + 'px'}
          />
        </LazyImage>
      )}
      <div className='c-card__content'>
        <div className='c-card__header'>
          {label && <p className='c-card__label'>{label}</p>}
          <CardTitle as={headlineLevel}>
            {url && (
              <a
                className='c-card__title-link'
                href={url}
                title={buttonLabel}
                target={openInNewTab ? '_blank' : '_self'}
                rel='noreferrer'
                dangerouslySetInnerHTML={{ __html: headline }}
                tabIndex={-1}
              ></a>
            )}
            {hasLightboxContent && (
              <button
                type='button'
                className='c-card__title-link'
                title={buttonLabel}
                onClick={() => lightboxRef.current.open()}
                dangerouslySetInnerHTML={{ __html: headline }}
              ></button>
            )}
            {!hasLightboxContent && !url && <>{headline}</>}
          </CardTitle>
          {subheadline && (
            <p className='c-card__subtitle' dangerouslySetInnerHTML={{ __html: subheadline }} />
          )}
        </div>
        {description && (
          <div
            className='c-card__description wysiwyg-block'
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
      <div className='c-card__footer'>
        {priceString && (
          <div
            className='c-card__price'
            dangerouslySetInnerHTML={{
              __html: priceString
            }}
          />
        )}
        {url && !hideButton && (
          <a
            ref={buttonRef}
            href={url}
            title={buttonLabel}
            aria-label={buttonLabel}
            className='c-button c-card__button'
            target={openInNewTab ? '_blank' : '_self'}
            rel='noreferrer'
          >
            {buttonText}
          </a>
        )}
        {hasLightboxContent && lightboxDescription && (
          <>
            <button
              ref={buttonRef}
              type='button'
              title={buttonLabel}
              aria-label={buttonLabel}
              className='c-button c-card__button'
              onClick={() => lightboxRef.current.open()}
            >
              {buttonText}
            </button>

            <ContentLightbox lightboxRef={lightboxRef} headline={headline} size='md' type='boxed'>
              <div className='c-card__lightbox'>
                <div
                  className='wysiwyg-block'
                  dangerouslySetInnerHTML={{
                    __html: lightboxDescription
                  }}
                />
                {image && (
                  <>
                    <LazyImage
                      style={{ marginTop: '20px' }}
                      placeholderUrl={image.sizes.placeholder}
                      defaultUrl={image.sizes.medium_large}
                      alt={image.alt}
                    >
                      <LazySource
                        srcSet={image.sizes.large}
                        mediaMin={tailwindConfig.theme.screens.xl + 'px'}
                      />
                      <LazySource
                        srcSet={image.sizes.medium_large}
                        mediaMin={tailwindConfig.theme.screens.lg + 'px'}
                      />
                      <LazySource
                        srcSet={image.sizes.large}
                        mediaMin={tailwindConfig.theme.screens.md + 'px'}
                      />
                      <LazySource
                        srcSet={image.sizes.medium_large}
                        mediaMin={tailwindConfig.theme.screens.sm + 'px'}
                      />
                    </LazyImage>
                  </>
                )}
              </div>
            </ContentLightbox>
          </>
        )}
      </div>
    </CardHTMLTag>
  );
};

Card.propTypes = {
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  description: PropTypes.string,
  hasLightboxContent: PropTypes.bool,
  lightboxDescription: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.bool]),
  url: PropTypes.string,
  hideButton: PropTypes.bool,
  fromPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fixPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  label: PropTypes.string,
  onLightboxToggle: PropTypes.func,
  openInNewTab: PropTypes.bool,
  headlineLevel: PropTypes.string
};

export default Card;
